import * as yup from 'yup';

import { languageEnabledArray, localizableString } from '@/helpers/validation.helper';
import type { Locales } from '@/services/types/api.types';
import { getSupportedLocales, i18n } from '@/plugins/i18n';

const { t } = i18n.global;

const optionalHexColor = yup
  .string()
  .trim()
  .nullable()
  .notRequired()
  .matches(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, {
    excludeEmptyString: true,
    message: () => t('validation.format', { field: t('community.branding.validation.color') }),
  });

const brandingFontSchema = yup.object({
  source: yup.mixed<'builtin' | 'remote-css'>().oneOf(['builtin', 'remote-css']).nullable().notRequired(),
  family: yup
    .string()
    .trim()
    .nullable()
    .when('source', {
      is: (value: string | null | undefined) => Boolean(value),
      then: (schema) =>
        schema.required(() => t('validation.required', { field: t('community.branding.validation.font_family') })),
      otherwise: (schema) => schema.notRequired(),
    }),
  builtin_key: yup
    .string()
    .trim()
    .nullable()
    .when('source', {
      is: 'builtin',
      then: (schema) =>
        schema.required(() => t('validation.required', { field: t('community.branding.validation.builtin_font') })),
      otherwise: (schema) => schema.notRequired(),
    }),
  css_url: yup
    .string()
    .trim()
    .nullable()
    .when('source', {
      is: 'remote-css',
      then: (schema) =>
        schema
          .required(() => t('validation.required', { field: t('community.branding.validation.remote_font_url') }))
          .url(() => t('validation.format', { field: t('community.branding.validation.remote_font_url') })),
      otherwise: (schema) => schema.notRequired(),
    }),
  fallback: yup.string().trim().nullable().notRequired(),
});

export const communityValidationSchema = (languageEnabled: Locales[]) =>
  yup.object({
    title: localizableString(languageEnabled),
    description: localizableString(languageEnabled),
    slug: yup
      .string()
      .required(() => t('validation.required', { field: 'slug' }))
      .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, () => t('validation.format', { field: 'slug' })),
    language_enabled: languageEnabledArray(),
    footer_description: localizableString(languageEnabled, { nullable: true, optional: true }).nullable(),
    options: yup
      .object()
      .shape({
        google_tag_manager: yup
          .object()
          .shape({
            enabled: yup.boolean().required(),
            container_id: yup.string().when('enabled', {
              is: true,
              then: (schema) => schema.required(() => t('validation.required', { field: 'GTM Container Id' })),
              otherwise: (schema) => schema.notRequired(),
            }),
          })
          .required(),
        featured_project_id: yup.number().notRequired(),
        allow_indexing: yup.boolean().required(),
        branding: yup
          .object()
          .shape({
            mode: yup.mixed<'default' | 'custom'>().oneOf(['default', 'custom']).nullable().notRequired(),
            preset_key: yup.string().trim().nullable().notRequired(),
            tokens: yup
              .object()
              .shape({
                colors: yup
                  .object()
                  .shape({
                    bg: optionalHexColor,
                    surface: optionalHexColor,
                    surface_raised: optionalHexColor,
                    surface_hover: optionalHexColor,
                    surface_active: optionalHexColor,
                    text: optionalHexColor,
                    muted: optionalHexColor,
                    text_subtle: optionalHexColor,
                    disabled: optionalHexColor,
                    brand: optionalHexColor,
                    brand_contrast: optionalHexColor,
                    link: optionalHexColor,
                    link_hover: optionalHexColor,
                    border: optionalHexColor,
                    border_strong: optionalHexColor,
                    focus_ring: optionalHexColor,
                    success: optionalHexColor,
                    success_bg: optionalHexColor,
                    warning: optionalHexColor,
                    warning_bg: optionalHexColor,
                    danger: optionalHexColor,
                    danger_bg: optionalHexColor,
                    info: optionalHexColor,
                    info_bg: optionalHexColor,
                  })
                  .nullable()
                  .notRequired(),
                typography: yup
                  .object()
                  .shape({
                    font_size_display: yup.string().trim().nullable().notRequired(),
                    font_size_headline: yup.string().trim().nullable().notRequired(),
                    font_size_title: yup.string().trim().nullable().notRequired(),
                    font_size_body: yup
                      .string()
                      .trim()
                      .nullable()
                      .notRequired()
                      .transform((v) => (!v || !v.trim() ? null : v)),
                    font_size_small: yup.string().trim().nullable().notRequired(),
                    leading_display: yup.string().trim().nullable().notRequired(),
                    leading_headline: yup.string().trim().nullable().notRequired(),
                    leading_body: yup
                      .string()
                      .trim()
                      .nullable()
                      .notRequired()
                      .transform((v) => (!v || !v.trim() ? null : v)),
                    tracking_display: yup.string().trim().nullable().notRequired(),
                    tracking_headline: yup.string().trim().nullable().notRequired(),
                    font_size_body_mobile: yup
                      .string()
                      .trim()
                      .nullable()
                      .notRequired()
                      .transform((v) => (!v || !v.trim() ? null : v)),
                    leading_body_mobile: yup
                      .string()
                      .trim()
                      .nullable()
                      .notRequired()
                      .transform((v) => (!v || !v.trim() ? null : v)),
                  })
                  .nullable()
                  .notRequired(),
              })
              .nullable()
              .notRequired(),
            fonts: yup
              .object()
              .shape({
                heading: brandingFontSchema.nullable().notRequired(),
                body: brandingFontSchema.nullable().notRequired(),
              })
              .nullable()
              .notRequired(),
          })
          .nullable()
          .notRequired(),
      })
      .required(),
    logo_file: yup
      .object()
      .shape({
        color: yup.string().required(),
        url: yup.string().optional().nullable(),
        altText: localizableString(languageEnabled, { optional: true }),
      })
      .required(),
    banner_file: yup
      .object()
      .shape({
        url: yup.string().optional(),
        altText: localizableString(languageEnabled, { optional: true }),
      })
      .nullable(),
    open_graph_file: yup
      .object()
      .shape({
        url: yup.string().optional(),
        altText: localizableString(languageEnabled, { optional: true }),
      })
      .nullable(),
    horizontal_logo_file: yup
      .object()
      .shape({
        url: yup.string().optional(),
        altText: localizableString(languageEnabled, { optional: true }),
      })
      .nullable(),
    default_project_theme_key: yup.string().required(),
    default_page_theme_key: yup.string().required(),
    default_language: yup.string().length(2).oneOf(getSupportedLocales()).required(),
  });
